'use client';

import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { SacFilterProps } from '@sola/schema/lib/schema.js';
import { TrackViewSacItemList } from '@sola/tracking/components/datalayer.js';

import { CardList } from '../card-list/card-list';
import { FeatureButtons } from '../feature-buttons/feature-buttons';
import { SacCard } from '../sac-card/sac-card';
import { SacEmpty } from '../sac-empty/sac-empty';
import { SacListSkeleton } from './sac-list.skeleton';
import { useSacChildren } from './useSacChildren';

type SacListLayoutProps = {
  allowShowMore?: boolean;
  columns?: '1' | '2';
  columnsSm?: '1' | '2';
  columnsMd?: '1' | '2' | '3';
  columnsLg?: '1' | '2' | '3';
};

const defaultLimit = 12;

const InternalSacList: React.FC<SacFilterProps & SacListLayoutProps> = ({
  limit = defaultLimit,
  gender,
  ageMin,
  ageMax,
  countries,
  allowShowMore = false,
  columns = 1,
  columnsSm = 1,
  columnsMd = 2,
  columnsLg = 3,
  hiv,
  orphan,
  disability,
  highlyVulnerable,
}) => {
  const filters = {
    limit: defaultLimit,
    gender,
    ageMin,
    ageMax,
    countries,
    allowShowMore: false,
    columns,
    columnsSm,
    columnsMd,
    columnsLg,
    hiv,
    orphan,
    disability,
    highlyVulnerable,
  };
  const { t } = useTranslation(['children']);
  const {
    isLoading,
    children,
    showRichMix,
    showLoadMoreButton,
    incrementPage,
    resetFilters,
  } = useSacChildren(filters, allowShowMore);
  const [selectedChild, setSelectedChild] = useState('');

  if (isLoading) {
    return <SacListSkeleton itemCount={limit} />;
  }

  if (!children?.length) {
    return <SacEmpty resetFilters={resetFilters} />;
  }

  const displayedChildren = children;

  return (
    <>
      {displayedChildren && (
        <>
          <CardList
            columns={Number(columns)}
            columnsSm={Number(columnsSm)}
            columnsMd={Number(columnsMd)}
            columnsLg={Number(columnsLg)}
            classNameGap="gap-y-roomy-xs gap-x-comfy-xl"
          >
            {displayedChildren.map((child) => (
              <SacCard
                key={child.globalId}
                selectedChild={selectedChild}
                setSelectedChild={setSelectedChild}
                {...child}
              />
            ))}
          </CardList>
          <TrackViewSacItemList
            listId="sac"
            listName="Sac List"
            items={displayedChildren}
          />
        </>
      )}

      {!showLoadMoreButton && (
        <div className="w-full text-center pt-roomy-xs body-md max-w-sm mx-auto">
          {showRichMix && t('children:sac.useSearchControls')}
          {!showRichMix && t('children:sac.useClearFilters')}
        </div>
      )}

      {allowShowMore && (
        <FeatureButtons
          variant="grow"
          className="w-full justify-center mt-comfy-xl md:mt-roomy-xs"
        >
          {showLoadMoreButton && (
            <ButtonStyle>
              <button type="button" onClick={() => incrementPage()}>
                {t('children:sac.viewMoreChildren')}
              </button>
            </ButtonStyle>
          )}
          {!showLoadMoreButton && !showRichMix && (
            <ButtonStyle variant="secondary">
              <button type="button" onClick={resetFilters}>
                {t('children:sac.clearFilters')}
              </button>
            </ButtonStyle>
          )}
          {/* @TODO: can these be content managed? */}
          <ButtonStyle
            variant={
              !showLoadMoreButton && !showRichMix ? 'primary' : 'secondary'
            }
          >
            <Link
              href="/sponsor-a-child/find-your-match/"
              className="w-full md:w-fit"
              type="button"
            >
              {t('children:sac.helpFindMatch')}
            </Link>
          </ButtonStyle>
        </FeatureButtons>
      )}
    </>
  );
};

export const SacList: React.FC<SacFilterProps & SacListLayoutProps> = (
  props,
) => {
  return (
    <Suspense
      // eslint-disable-next-line react/destructuring-assignment
      fallback={<SacListSkeleton itemCount={props.limit || defaultLimit} />}
    >
      <InternalSacList {...props} />
    </Suspense>
  );
};
